// English strings (source language): race events (HUD notifications), from the local player's point of view.
// `who` is null when it is the local player.

const en = {
  hit: (who: string | null, kind: 'banana' | 'shell') =>
    `${who === null ? 'You' : who} ${kind === 'banana' ? '🍌 slipped on a banana' : '🎯 got hit by a missile'}`,
  /** Down in a crash; `via`: the fallen rider they rode into. */
  crashed: (who: string | null, via: string | null) => `💥 ${who === null ? 'You are' : who + ' is'} down${via === null ? '' : ` after riding into ${via}`}`,
  /** Hit by a created item (community trap or missile). */
  hitCustom: (who: string | null, icon: string, name: string) => `${who === null ? 'You' : who} got hit by ${icon} ${name}`,
  /** An opponent uses a created item against you. */
  customOn: (icon: string, who: string, name: string) => `${icon} ${who}: ${name} on you!`,
  wellPlayed: ' (nice one!)',
  thanks: (name: string) => ` — thanks ${name}`,
  by: (name: string) => ` (${name})`,
  blockedWhat: { banana: 'the banana', shell: 'the missile', jam: 'the jammed derailleur', storm: 'the storm', fog: 'the fog', crash: 'the crash', other: 'the attack' },
  blocked: (who: string | null, what: string) => `🛡️ ${who === null ? 'Your shield' : who + '’s shield'} blocked ${what}`,
  on: (name: string) => ` on ${name}`,
  straightAhead: ' straight ahead',
  onRiders: (n: number) => ` on ${n} rider${n === 1 ? '' : 's'}`,
  bombGo: '💣 Sprint all-out for 5 s!',
  shellIncoming: (who: string) => `⚠️ ${who} fired a missile at you!`,
  jammed: (who: string) => `🔒 ${who} jammed your derailleur!`,
  stormIncoming: (who: string) => `⛈️ ${who} sent a headwind storm at you!`,
  fogIncoming: (who: string) => `🌫️ ${who} sent fog at you`,
  stormOn: (name: string) => `⛈️ Storm on ${name}`,
  deny: '🔒 Derailleur jammed!',
  bomb: (avg: number, pct: number, boost: number) => `💥 Average ${avg} W (${pct}% FTP) → boost +${boost}%`,
  lastLap: '🔔 Last lap!',
  lap: (lap: number, laps: number | null) => `Lap ${lap}${laps ? '/' + laps : ''}`,
  finishMe: (result: string) => `🏁 Finish! ${result}`,
  finishOther: (name: string, rank: string) => `🏁 ${name} finishes ${rank}`,
  elimLast: (lap: number) => `last on lap ${lap}`,
  elim: (who: string | null, why: string) => `❌ ${who === null ? 'You are eliminated' : who + ' is eliminated'} (${why})`,
  round: (lap: number, left: number) => `Lap ${lap} · ${left} riders left`,
  left: (name: string) => `👋 ${name} left the game, a bot takes their place`,
  /** A supporter cheers; `who` is null when it is for the local player. */
  cheer: (from: string, who: string | null, emoji: string) => (who === null ? `${emoji} ${from} cheers you on!` : `${emoji} ${from} cheers for ${who}`),
  cheerBoost: ' · crowd boost!',
  /** Events screen (calendar, detail page, workshop) and event room card. */
  screen: {
    onDemand: 'On demand',
    ended: (when: string) => `Finished · ${when}`,
    inProgress: 'In progress',
    startsIn: (min: number) => `Starts in ${min} min`,
    lineOpen: (min: number) => `Start line open · gun in ${min} min`,
    noFixedDistance: 'no fixed distance',
    stagesKm: (n: number, km: string) => `${n} stages · ${km}`,
    byOwner: (name: string) => `by ${name}`,
    enrolled: (cat: string) => `Entered · ${cat}`,
    title: 'Events',
    intro:
      'Scheduled starts by category, like virtual races, and multi-stage tours with jerseys and a general classification. Describe your own in a single sentence.',
    moreDays: (n: number) => `Show the next ${n} days`,
    tabCalendar: 'Calendar',
    tabTours: 'Tours and races',
    tabMine: 'My events',
    tabCreate: 'Create',
    loading: 'Loading…',
    offline: 'Server unreachable: only the game’s events and your own saved here are shown.',
    yourCat: (badge: string, score: string) =>
      `Your Gruvetto score: ${score} points, category ${badge}. Without category control, you choose freely.`,
    toursTitle: 'Stage tours',
    oneDayTitle: 'One-day races',
    noTour: 'No tours.',
    noRace: 'No races.',
    organisedTitle: 'Organised by me',
    newBtn: 'New',
    nothingOrganised: 'You haven’t organised anything yet. Describe a tour in one sentence under “Create”.',
    joinedTitle: 'My entries',
    noJoined: 'No entries.',
    // Leaderboards
    noResultInCat: 'No results in this category yet.',
    stageNotRun: 'Stage not ridden yet.',
    colRider: 'Rider',
    colPoints: 'Points',
    colTime: 'Time',
    colGap: 'Gap',
    colStages: 'Stages',
    colMountain: 'Mountain',
    colBonus: 'Bonus',
    youTag: 'you',
    sameTime: 's.t.',
    estimatedTitle: 'Time estimated at the end of the race',
    unitHits: 'hits',
    unitWins: 'wins',
    unitPts: 'pts',
    seconds: (n: number) => `${n} s`,
    nobodyScored: 'Nobody has scored here yet.',
    // Detail page
    notFoundTitle: 'Event not found',
    notFoundBody: 'It may have been deleted, or it is private.',
    backToCalendar: 'Back to the calendar',
    tourStages: (n: number) => `Tour · ${n} stages`,
    oneDay: 'One-day race',
    gcByPoints: 'General by points',
    gcByTime: 'General by time',
    minLevel: (n: number) => `level ${n} minimum`,
    onlineStart: 'Online start',
    joinLine: 'Join the start line',
    joinInProgress: 'Join the race in progress',
    opensAt: (time: string) => `Start line opens at ${time}`,
    startGiven: 'Start given',
    tourDone: 'Tour finished',
    raceDone: 'Race finished',
    edit: 'Edit',
    remove: 'Delete',
    copyEdit: 'Copy and edit',
    copyLink: 'Copy link',
    catsTitle: 'Categories',
    enforced: 'Category control: you can ride higher, never lower.',
    freeChoice: 'Free choice of category.',
    youAreAt: (score: string) => `Your score: ${score} points.`,
    botsMatched: 'Bots fill the empty places',
    extraLapsPerStage: (n: number) => ` · ${n > 0 ? '+' : ''}${n} lap${Math.abs(n) === 1 ? '' : 's'} per stage`,
    entrants: (n: number) => `${n} entered`,
    lockedForYou: 'closed to you',
    enrolledIn: (cat: string) => `Entered in ${cat}.`,
    suggestedCat: (cat: string) => `Suggested category: ${cat}.`,
    yourBots: 'Bots in this category:',
    withdraw: 'Withdraw',
    noOpenCat: 'No category is open to you in this event.',
    stagesTitle: 'Stages',
    courseTitle: 'Course',
    minutes: (n: number) => `${n} min`,
    untilLast: 'until the last rider',
    laps: (n: number) => `${n} lap${n === 1 ? '' : 's'}`,
    sprintsCount: (n: number) => `${n} sprint${n === 1 ? '' : 's'}`,
    summitsCount: (n: number) => `${n} summit${n === 1 ? '' : 's'}`,
    segmentsCount: (n: number) => `${n} contested segment${n === 1 ? '' : 's'}`,
    noItems: ' · no items',
    itemsCount: (n: number, all: number) => ` · ${n} of ${all} items`,
    replay: 'Ride again',
    jerseysTitle: 'Jerseys',
    leader: 'Leader:',
    youLeader: ' (you!)',
    notAwarded: 'Not awarded yet',
    leadsBy: (gap: string, name: string) => `Leads ${name} by ${gap}`,
    tiedWith: (name: string) => `Level with ${name}`,
    leaderElsewhere: (name: string, jersey: string) => `Leader: ${name}, who wears the ${jersey}`,
    leaderIs: (name: string) => `Leader: ${name}`,
    ptsN: (n: number) => `${n} pts`,
    hitsN: (n: number) => `${n} hit${n === 1 ? '' : 's'}`,
    winsN: (n: number) => `${n} win${n === 1 ? '' : 's'}`,
    gcChartTitle: (n: number) => `Gap to the leader after each stage, top ${n} of the general`,
    stageTitle: (n: number, name: string) => `Stage ${n}: ${name}`,
    takes: (who: string, jersey: string) => `${who} takes the ${jersey}`,
    takesFrom: (who: string, jersey: string, from: string) => `${who} takes the ${jersey} from ${from}`,
    standingsTitle: 'Standings',
    boardPersonal: 'My tour',
    boardPlayers: 'Between players',
    catOption: (cat: string) => `Category ${cat}`,
    general: 'General',
    ranking: 'Standings',
    stageShort: (n: number) => `St. ${n}`,
    playersNote: 'Times and points add up from stage to stage, across the starts each player rode.',
    noCatToast: 'No category is open to you',
    levelRequired: (n: number) => `Level ${n} required`,
    changeCatConfirm: (cat: string) => `Change category? Stages you have already ridden stay in ${cat}.`,
    change: 'Change',
    enrolledToast: (cat: string) => `Entered in category ${cat}`,
    linkCopied: 'Link copied',
    copySuffix: ' (copy)',
    deleteConfirm: (name: string) => `Delete “${name}” and its results?`,
    // Workshop
    examples: [
      'A 5-stage Tour de France style tour for categories C and D, with a mountain stage, a time trial and a sprint finish',
      'A sprinters’ race on Saturday at 6 pm on the criterium, 6 laps and 3 intermediate sprints',
      'Points championship in 4 rounds: Grand Prix, time trial, elimination and a final, with a combativity jersey',
      'Climbers’ triptych, 3 stages without items, the As ride one extra lap',
    ],
    blankName: 'My event',
    stageN: (n: number) => `Stage ${n}`,
    cannotEditTitle: 'Cannot edit',
    cannotEditBody: 'Only the organiser can edit this event.',
    editTitle: 'Edit event',
    createTitle: 'Create an event',
    editorIntro: 'Describe it in plain words: the AI prepares the stages, categories and jerseys. The game checks everything, then you fine-tune by hand.',
    tweakHead: 'Tweak in one sentence',
    describeHead: 'Describe your event',
    phTweak: 'Harder, add a mountain stage, start Sunday at 10 am…',
    phNew: 'A 4-stage tour for my friends…',
    generating: 'Generating…',
    tweakBtn: 'Tweak',
    generate: 'Generate',
    startOver: 'Start over',
    manual: 'Start from an empty form',
    srcClaude: 'Claude AI',
    srcLocal: 'Local generator',
    writeMore: 'Write at least a few words',
    settingsTitle: 'Settings',
    fName: 'Name',
    fEmoji: 'Emoji',
    fStart: 'Start (empty = on demand)',
    fDesc: 'Description',
    fCats: 'Categories',
    fEnforce: 'Category control',
    enforceYes: 'Yes (Gruvetto score)',
    enforceNo: 'No, free choice',
    fMinLevel: 'Minimum level',
    fStageStart: 'Stage start',
    fBots: 'Bots per category',
    fGc: 'General classification',
    gcTime: 'By time',
    gcPoints: 'By points',
    fBonuses: 'Time bonuses',
    fLateJoin: 'Join in progress',
    bonusesYes: 'Yes (10/6/4 s, sprints 3/2/1 s)',
    no: 'No',
    yes: 'Yes',
    fExtraLaps: 'Extra laps per category',
    addStage: '+ Stage',
    fMode: 'Mode',
    fTrack: 'Track',
    fLaps: 'Laps',
    fSprints: 'Sprints (% of the distance)',
    sprintsPh: 'e.g. 30, 70',
    fMountain: 'Mountain',
    mountainYes: 'Points at the summits',
    fSegments: 'Contested segments',
    segNone: 'Not contested',
    segRule: (by: 'first' | 'fastest', objective: 'sprint' | 'mountain') => `${by === 'first' ? 'First past the end' : 'Fastest through'} · ${objective === 'sprint' ? 'sprint points' : 'mountain points'}`,
    fItems: 'Items',
    fItemPool: 'Items in the boxes',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeStage: 'Delete',
    fVisibility: 'Visibility',
    visPrivate: 'Private: only me',
    visLink: 'By link: whoever I send it to',
    visPublic: 'Public: in everyone’s calendar',
    save: 'Save',
    createBtn: 'Create the event',
    corrections: 'Game corrections',
    ok: 'OK',
    saved: 'Event saved',
    created: 'Event created 🎉',
    // Names of built-in content (categories, stage profiles, bot specialities, jerseys)
    cats: {
      A: { name: '600+ pts', desc: '600 points and above' },
      B: { name: '440–599 pts', desc: '440 to 599 points' },
      C: { name: '300–439 pts', desc: '300 to 439 points' },
      D: { name: '0–299 pts', desc: 'under 300 points' },
      E: { name: 'Open', desc: 'every score' },
    } as Record<string, { name: string; desc: string }>,
    profiles: { plat: 'Flat', vallonne: 'Rolling', montagne: 'Mountain', clm: 'Time trial' } as Record<string, string>,
    specialties: { grimpeur: 'Climber', sprinteur: 'Sprinter', rouleur: 'Rouleur', puncheur: 'Puncheur' } as Record<string, string>,
    jerseys: {
      gc: { name: 'Yellow jersey', desc: 'General classification leader' },
      points: { name: 'Green jersey', desc: 'Points classification: finishes and intermediate sprints' },
      mountain: { name: 'Polka-dot jersey', desc: 'Best climber: points at the top of climbs' },
      rookie: { name: 'White jersey', desc: 'Best rookie in the general (level 10 or below)' },
      sprint: { name: 'Red jersey', desc: 'King of the intermediate sprints' },
      combative: { name: 'Combativity jersey', desc: 'Most hits with items' },
      wins: { name: 'Rainbow jersey', desc: 'Most stage wins' },
    } as Record<string, { name: string; desc: string }>,
    // Event card in the online room
    room: {
      label: 'EVENT',
      stageOf: (n: number, total: number) => ` · STAGE ${n}/${total}`,
      autoStart: 'Gun at the scheduled time',
      onTime: 'The start is given at the scheduled time, for everyone: wait at the line.',
      needTwo: 'Waiting for another rider: an event starts with at least two riders at the line. Share the link to invite someone.',
      note: 'Settings come from the stage. Each category has its own bots and standings.',
    },
  },
  // Event stage during the ride: HUD banner, checkpoints, end-of-stage summary
  stageRide: {
    atStart: 'At the start',
    stageOf: (n: number, total: number) => `Stage ${n}/${total} · `,
    cat: (c: string) => c,
    catBadge: (c: string) => c,
    komLabel: (cat: number) => (cat <= 0 ? 'beyond category' : `category ${cat}`),
    sprintIn: (dist: string) => `Sprint in ${dist}`,
    summitIn: (label: string, dist: string) => `Summit, ${label}, in ${dist}`,
    passes: (who: string | null, rank: string, kind: 'sprint' | 'kom') => `${who === null ? 'You pass' : `${who} passes`} ${rank} ${kind === 'sprint' ? 'at the sprint' : 'at the summit'}`,
    segmentIn: (name: string, len: string) => `${name} · ${len}`,
    segmentDone: (name: string, time: string) => `${name}: ${time}`,
    segmentTop: ' · fastest of the race',
    segmentBest: ' · your best today',
    segmentTopBy: (who: string, name: string, time: string) => `${who} sets the fastest time on ${name}: ${time}`,
    keptLocally: (error: string) => `Stage result kept here, could not send it to the server: ${error}`,
    stageCol: (c: string) => `Stage · ${c}`,
    teamsCol: (c: string) => `Teams · ${c}`,
    mineTeam: (team: string, rank: string, of: number) => `Team ${team}: ${rank} of ${of}`,
    general: 'General',
    points: 'Points',
    time: 'Time',
    gap: 'Gap',
    pts: (n: number) => `${n} pts`,
    sameTime: 's.t.',
    you: ' (you!)',
    mine: (rank: string, of: number) => `Stage: ${rank} of ${of}`,
    plusPts: (n: number) => ` · +${n} pts`,
    plusKom: (n: number) => ` · +${n} mountain pts`,
    bonus: (s: number) => ` · ${s} s of bonus seconds`,
    gcRank: (rank: string) => ` · ${rank} overall`,
    titleStage: (n: number, total: number) => ` · stage ${n}/${total}`,
  },
};
export default en;
